// main imports
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// backend
import { getBackend } from "@/lib/backend/Backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as Auth from "@/lib/Auth";
import * as Util from "@/lib/Util";

// import subcomponents
import PeopleCompetence from "./PeopleCompetence";
import PeopleCompetenceModal from "./PeopleCompetenceModal";
import ErrorComponent from "@/components/ErrorComponent";
import { translate } from "@/components/IntlProviderWrapper";
import ReactTable, { ReactTableColumn } from "@/components/react/ReactTable";

// defining types
type Props = {
    selected: t.IPersonData;
    lines: t.ILineData[];
    plants: t.IPlantData[];
    standalone?: boolean;
};

type State = {
    show_modal: boolean;
    competences: t.ILineCompetenceData[];
    shifts: t.IShiftsPeopleEx[];
    error: string;
    warning: string;
};

/////////////////////////////////////////
// MAIN COMPONENT

/**
 * Displaying people details and manage competences.
 */
class PeopleDetails extends Component<Props, State> {
    state: State = {
        show_modal: false,
        competences: [],
        shifts: [],
        error: "",
        warning: "",
    };

    /** Load competences on first load */
    async componentDidMount() {
        await this.loadCompetences();
        await this.loadSchedule();
    }

    /**
     * Component updates by clicking another person. Competences need to be refreshed
     * from API.
     */
    async componentDidUpdate(prevProps: Props) {
        // we need to trigger reload only if it has not been triggered before
        if (prevProps.selected.uuid !== this.props.selected.uuid) {
            await this.loadCompetences();
            await this.loadSchedule();
        }
    }

    async loadCompetences() {
        try {
            const person_uuid = this.props.selected.uuid;
            const data = await getBackend().manufacturing.getLineCompetence({ person_uuid });
            this.setState({
                competences: data.competences,
                error: "",
            });
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    async loadSchedule() {
        try {
            const person_uuid = this.props.selected.uuid;
            const data = await getBackend().manufacturing.getPeopleShiftsSimple({
                from_shift: Util.shiftNumber(Date.now() - Util.TIME_RANGES.WEEK).shift_tag,
                person_uuid,
                to_shift: Util.shiftNumber(Date.now() + Util.TIME_RANGES.WEEK).shift_tag,
            });
            this.setState({ shifts: data.shifts_people, error: "" });
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    /**
     * Method triggered when Add/Update/Edit button is clicked. It opens competences modal.
     */
    handleModalShowClick() {
        this.setState({ show_modal: true });
    }

    /**
     * Method triggered when modal close button (or modal background overlay) is clicked.
     * It closes the modal.
     */
    async handleModalCloseClick(do_refresh: boolean) {
        if (do_refresh) {
            await this.loadCompetences();
        }

        this.setState({ show_modal: false });
    }

    renderTags() {
        const keys = Object.keys(this.props.selected.tags).sort((a, b) => a.localeCompare(b));
        const result: React.ReactNode[] = [];
        for (const key of keys) {
            result.push(
                <div key={key}>
                    <dt>{translate(`Manufacturing.People.${key}`, key)}</dt>
                    <dd>{this.props.selected.tags[key]}</dd>
                </div>
            );
        }
        return result;
    }

    renderCompetences() {
        const { competences } = this.state;
        if (competences.length === 0) {
            return null;
        }

        competences.sort((a, b) => {
            if (a.plant_title !== b.plant_title) {
                return Util.stringCompare(a.plant_title, b.plant_title);
            }

            return Util.stringCompare(a.line_title, b.line_title);
        });

        const plant_uuids = [...new Set(competences.map(x => x.plant_uuid))];

        return (
            <>
                {plant_uuids.map(plant_uuid => {
                    const competences_for_plant = competences.filter(x => x.plant_uuid === plant_uuid);
                    return (
                        <React.Fragment key={plant_uuid}>
                            <h5>{competences_for_plant[0].plant_title}</h5>
                            <ul className="list-group list-group-flush">
                                {competences_for_plant.map((competence, i) => (
                                    <PeopleCompetence
                                        key={`competence_${plant_uuid}_${i}`}
                                        level={competence.level}
                                        title={competence.line_title}
                                    />
                                ))}
                            </ul>
                        </React.Fragment>
                    );
                })}
            </>
        );
    }

    renderSchedule() {
        if (this.state.shifts.length == 0) {
            return null;
        }
        const columns: ReactTableColumn<t.IShiftsPeopleEx>[] = [
            {
                Header: translate("common.shift", "Shift"),
                accessor: "shift_tag",
                disableFilters: true,
                Cell: val => {
                    const rec: t.IShiftsPeopleEx = val.cell.row.original;
                    const d = Util.fromShiftTag(rec.shift_tag);
                    return Util.niceDateTime(d);
                },
            },
            {
                Header: translate("common.line", "Line"),
                accessor: "line_title",
                disableFilters: true,
            },
        ];
        const data = this.state.shifts.sort((a, b) => a.shift_tag.localeCompare(b.shift_tag));
        return (
            <div>
                <h3>
                    <FormattedMessage id="common.schedule" defaultMessage="Schedule" />
                </h3>
                <ReactTable data={data} columns={columns} />
            </div>
        );
    }

    render() {
        if (this.props.selected.uuid) {
            const { name, uuid, external_id, ted_id, adjuster, shift_preference, plant_uuids } = this.props.selected;
            const can_edit = Auth.hasPermission(Auth.PERMISSION_NAMES.PeopleEdit);

            const plants_hash = Object.fromEntries(this.props.plants.map(plant => [plant.uuid, plant.title]));
            const plants = plant_uuids.map(uuid => plants_hash[uuid]).join(", ");

            const shiftBadges = shift_preference.map((val, i) => {
                const shift = i + 1;
                return val ? (
                    <span key={i} className={"badge badge-shift badge-shift-" + shift}>
                        {shift}
                    </span>
                ) : null;
            });

            // if we are in standalone mode back link on edit page is different
            const edit_url = this.props.standalone
                ? `/digital-twin/resources/person/edit/${uuid}?back=view`
                : `/digital-twin/resources/person/edit/${uuid}`;

            const groupped_competences = this.renderCompetences();

            return (
                <div>
                    <ErrorComponent msg={this.state.error} type="error" />
                    <ErrorComponent msg={this.state.warning} type="warning" />
                    <div className="tab-content">
                        <div className="tab-pane active tab-pane-text" id="home">
                            <div className="d-flex">
                                {can_edit && (
                                    <div className="button-list order-last ml-auto">
                                        <Link className="btn btn-outline-primary" to={edit_url}>
                                            <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                        </Link>
                                    </div>
                                )}
                                <div className="order-first">
                                    <h5 className="pane-title">
                                        {name}{" "}
                                        {adjuster && (
                                            <span className="badge-list">
                                                <span className="badge badge-primary">
                                                    <FormattedMessage id="common.adjuster" defaultMessage="adjuster" />
                                                </span>
                                            </span>
                                        )}
                                    </h5>
                                    <dl className="property-list">
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="common.personel_id"
                                                    defaultMessage="Personel ID"
                                                />
                                            </dt>
                                            <dd>
                                                <div>{external_id}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage id="common.linegroup" defaultMessage="Line-group" />
                                            </dt>
                                            <dd>
                                                <div>{ted_id}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="Manufacturing.People.allowed_shifts"
                                                    defaultMessage="Allowed shifts"
                                                />
                                            </dt>
                                            <dd>
                                                <span className="badge-list">{shiftBadges}</span>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="Manufacturing.People.plant_membership"
                                                    defaultMessage="Plant membership"
                                                />
                                            </dt>
                                            <dd>
                                                <div>{plants}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage id="common.tags" defaultMessage="Tags" />
                                            </dt>
                                            <div>{this.renderTags()}</div>
                                        </div>
                                        <div>
                                            <dt className="light">
                                                <FormattedMessage id="common.uuid" defaultMessage="UUID" />
                                            </dt>
                                            <dd className="light">
                                                <div>{uuid}</div>
                                            </dd>
                                        </div>
                                    </dl>
                                    {this.renderSchedule()}
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                                <h2 className="mr-2">
                                    <FormattedMessage
                                        id="Manufacturing.People.line_competences"
                                        defaultMessage="Line competences"
                                    />
                                </h2>
                                {can_edit && (
                                    <button
                                        className="btn btn-outline-primary ml-auto my-1"
                                        onClick={() => this.handleModalShowClick()}
                                    >
                                        <FormattedMessage
                                            id="Manufacturing.People.edit_competences"
                                            defaultMessage="Edit competences"
                                        />
                                    </button>
                                )}
                                {this.state.show_modal && (
                                    <PeopleCompetenceModal
                                        show={this.state.show_modal}
                                        lines={this.props.lines}
                                        person_uuid={this.props.selected.uuid}
                                        person_name={this.props.selected.name}
                                        plant_uuids={this.props.selected.plant_uuids}
                                        handleModalCloseClick={(do_refresh: boolean) => {
                                            this.handleModalCloseClick(do_refresh);
                                        }}
                                    />
                                )}
                            </div>
                            <div className="competences">
                                <div className="card">
                                    {groupped_competences !== null && groupped_competences}
                                    {groupped_competences === null && (
                                        <h5>
                                            <FormattedMessage
                                                id="Manufacturing.People.no_competences"
                                                defaultMessage="No competences"
                                            />
                                        </h5>
                                    )}
                                    <div className="space15"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // return empty element if there are no available competences
        return <div />;
    }
}

export default PeopleDetails;
